import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, isRedirect, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { isLocale, locales } from '@ticketai/i18n';
import { authClient } from '@/lib/auth-client';
import { isUnauthorized } from '@/lib/errors';
import { setLocale } from '@/lib/i18n';
import { client } from '@/lib/orpc';
import { meQuery, useMe } from '@/lib/session';

/**
 * Everything signed-in renders inside this shell. The guard loads the user once
 * (cached), sends anonymous visitors to /login, and anyone without a passkey to
 * /setup-passkey (mandatory, PLAN.md §4).
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery());
      if (!me.hasPasskey) throw redirect({ to: '/setup-passkey' });
      return { me };
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (isUnauthorized(e)) throw redirect({ to: '/login', search: { redirect: location.href } });
      throw e;
    }
  },
  component: Shell
});

const NAV = [
  { to: '/tickets', key: 'tickets' },
  { to: '/approvals', key: 'approvals' },
  { to: '/assistants', key: 'assistants' },
  { to: '/log', key: 'log' }
] as const;

function Shell() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const changeLocale = (value: string) => {
    if (!isLocale(value)) return;
    setLocale(value);
    // Mails use the stored locale; the UI follows at once.
    void client.me.update({ locale: value }).catch(() => {});
  };
  const signOut = async () => {
    await authClient.signOut();
    queryClient.clear();
    await navigate({ to: '/login' });
  };
  const links = (
    <>
      {NAV.map((n) => (
        <li key={n.to}>
          <Link to={n.to} activeProps={{ className: 'menu-active' }}>
            {t(`nav.${n.key}`)}
          </Link>
        </li>
      ))}
      {me.role === 'admin' && (
        <li>
          <Link to="/admin" activeProps={{ className: 'menu-active' }}>
            {t('nav.admin')}
          </Link>
        </li>
      )}
    </>
  );
  return (
    <div className="bg-base-200 min-h-screen">
      <header className="navbar bg-base-100 border-base-300 sticky top-0 z-10 border-b px-4">
        <div className="navbar-start gap-2">
          <div className="dropdown lg:hidden">
            <button type="button" tabIndex={0} className="btn btn-ghost btn-sm" aria-label={t('nav.menu')}>
              ☰
            </button>
            <ul tabIndex={0} className="menu dropdown-content bg-base-100 rounded-box z-20 mt-2 w-52 p-2 shadow">
              {links}
            </ul>
          </div>
          <Link to="/tickets" className="text-lg font-semibold">
            {t('app.name')}
          </Link>
        </div>
        <nav className="navbar-center hidden lg:flex">
          <ul className="menu menu-horizontal gap-1 px-1">{links}</ul>
        </nav>
        <div className="navbar-end gap-2">
          <select className="select select-sm w-auto" value={i18n.language} onChange={(e) => changeLocale(e.target.value)} aria-label={t('language.label')}>
            {locales.map((l) => (
              <option key={l} value={l}>
                {t(`language.${l}`)}
              </option>
            ))}
          </select>
          <div className="dropdown dropdown-end">
            <button type="button" tabIndex={0} className="btn btn-ghost btn-sm" data-testid="user-menu">
              {me.name}
            </button>
            <ul tabIndex={0} className="menu dropdown-content bg-base-100 rounded-box z-20 mt-2 w-48 p-2 shadow">
              <li>
                <Link to="/account">{t('nav.account')}</Link>
              </li>
              <li>
                <button type="button" onClick={() => void signOut()}>
                  {t('nav.signOut')}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
